import React from 'react';
import dompurify from 'dompurify';
import { SortType } from '@home/enums/common';

/**
 * 判断数据是否为undefined或者null
 * @param data 数据
 * @return 数据是否为undefined或者null
 */
export const isNullOrUndefined = (data?: any): data is null | undefined => {
  return data === void 0 || data === null;
};

/** 判断值能否作为候选值使用，保留 0、false、空数组和空对象 */
export const isAvailableValue = (value: unknown): boolean => {
  if (isNullOrUndefined(value)) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value) && Number.isFinite(value);
  }

  return true;
};

/**
 * 判断数据是否为空，以下场景数据判定为空，其余场景判定为非空
 * 1、undefined、null
 * 2、空白字符串（trim处理）
 * 3、NaN
 * 4、length为0的数组、有length属性并且length属性值为0的类似数据结构
 * 5、size为0的Set、Map、有size属性并且size属性值为0的类似数据结构
 * 6、可枚举属性数量为0的普通对象
 * @param data 数据
 * @return 数据是否为空
 */
export const isEmpty = (data?: any): data is null | undefined => {
  // 处理 null 和 undefined
  if (isNullOrUndefined(data)) return true;

  // 处理字符串（包括空白字符）
  if (typeof data === 'string') return data.trim() === '';

  // 处理数字（可选：添加 0 的判断）
  if (typeof data === 'number') return Number.isNaN(data);

  // 处理数组和类数组对象
  if (Array.isArray(data) || typeof data.length === 'number') {
    return data.length === 0;
  }

  // 处理 Set/Map 和类集合对象
  if (data instanceof Set || data instanceof Map || typeof data.size === 'number') {
    return data.size === 0;
  }

  // 处理普通对象（包括无原型的对象）
  if (Object.prototype.toString.call(data) === '[object Object]') {
    return Object.keys(data).length === 0;
  }

  // 其他类型视为非空
  return false;
};

/**
 * 判断指定数据是否非空，即isEmpty方法取反
 * @param data 数据
 * @return 数据是否非空
 */
export const isNotEmpty = <T = any>(data?: T | null | undefined): data is NonNullable<T> => {
  return !isEmpty(data);
};

/**
 * 延迟函数
 * @param ms - 延迟时间（毫秒）
 * @returns Promise<void>
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * 安全解析JSON字符串
 * @param defaultValue - 默认值，解析失败时会返回默认值
 * @param jsonString - JSON字符串
 * @return 解析结果
 */
export const parseJSONString = <T = any>(defaultValue: T, jsonString?: string): T => {
  if (isEmpty(jsonString)) {
    return defaultValue;
  }

  let result = defaultValue;

  try {
    result = JSON.parse(jsonString!);
  } catch (error) {
    console.error(error);
    result = defaultValue;
  }

  return result ?? defaultValue;
};

/**
 * 标准化 code
 * 用于模糊比较 code 是否相同
 * 1. 把 code 字符都转换为小写字符
 * 2. 删除字符串内的所有空格
 * @param code 待标准化的 code
 * @return 标准化的 code
 */
export const normalizeCode = (code?: string): string => {
  if (typeof code !== 'string') {
    return '';
  }

  return code.toLowerCase().replace(/\s/g, '');
};

/**
 * 净化values
 * 1. 判空守卫
 * 2. 去重
 * 3. 过滤空值
 * @param values 值
 * @return 值
 */
export const sanitizeValues = (values?: string[]): string[] => {
  if (isEmpty(values)) {
    return [];
  }

  return Array.from(new Set(values)).filter(isNotEmpty);
};

/**
 * 生成排序key（接口入参）（通用）
 * 格式: 字段名 desc|asc（中间有且仅有一个空格）
 * @param key 排序字段
 * @param sortType 排序方式
 */
export const generateSortKey = (key: string, sortType: SortType) => {
  if (sortType === SortType.None) return key;
  return `${key} ${sortType}`;
};

export const analysisSortKey = (key?: string): { sortKey: string; sortType: SortType } | null => {
  if (!key) return null;
  const [sortKey, sortType] = key.split(' ') as [string, SortType];
  return { sortKey, sortType };
};

/**
 * 折线图数据获取接口入参的order_by字段，固定为按日期升序
 */
export const LINE_CHART_ORDER_BY = [generateSortKey('date', SortType.Asc)];

/**
 * 解析list value的字符串，多个value通过"|"字符拼接
 * @param listString - list value字符串
 * @param splitter - 分隔字符，默认为"|"
 */
export const parseListString = (listString?: string, splitter = '|'): string[] => {
  const listStringTrim = listString?.trim() || '';

  if (isEmpty(listStringTrim)) {
    return [];
  }

  return [...new Set(listStringTrim.split(splitter).filter(Boolean))];
};

/**
 * 获取安全的HTML字符串（xss攻击防范处理）
 * @param htmlStr - HTML字符串
 */
export const getSanitizeHTMLString = (htmlStr?: string): string => {
  if (!htmlStr) {
    return '';
  }

  return dompurify.sanitize(htmlStr);
};

/**
 * 安全的 decodeURIComponent 封装
 * 原生 decodeURIComponent 在遇到非法编码序列（如 "%E0%A4%A"、单独的 "%" 等）时会抛出 URIError，
 * 该函数捕获异常并返回原始字符串，避免因 URL 参数编码异常导致页面崩溃。
 * @param str - 需要解码的 URI 编码字符串
 * @returns 解码后的字符串，解码失败时返回原始字符串，若原始字符串为 nullish 则返回空字符串
 */
export const safeDecodeURIComponent = (str: string): string => {
  try {
    return decodeURIComponent(str);
  } catch {
    return str ?? '';
  }
};

/**
 * 统一的国际化处理函数
 * @param formatMessage - i18n翻译的方法
 * @param text - 待翻译的文本
 * @return i18n翻译后的文本
 */
export const formatText = (
  formatMessage: (msg: { id: string }) => string,
  text: string | React.ReactNode,
): string | React.ReactNode => {
  if (typeof text === 'string') {
    try {
      return formatMessage({ id: text });
    } catch {
      return text;
    }
  }

  return text;
};

export const getValueByKeys = <T = any>(item: IRecord | undefined | null, keys: string[]): T | null => {
  if (isEmpty(keys)) {
    return null;
  }

  for (const key of keys) {
    const value = item?.[key];
    if (isAvailableValue(value)) {
      return value;
    }
  }

  return null;
};

export const getValueByItems = <T = any>(key: string, ...items: any[]): T | null => {
  if (isEmpty(key) || isEmpty(items)) {
    return null;
  }

  for (const item of items) {
    const value = item?.[key];
    if (isAvailableValue(value)) {
      return value;
    }
  }

  return null;
};

export const getLinkedValueByItems = <T extends IRecord<string>>(
  keyMap: T,
  ...items: any[]
): Record<keyof T, any> | null => {
  if (isEmpty(keyMap) || isEmpty(items)) {
    return null;
  }

  const keyEntries = Object.entries(keyMap) as [keyof T, string][];
  for (const item of items) {
    const result = {} as unknown as Record<keyof T, any>;
    let isValid = true;
    for (const [resultKey, itemKey] of keyEntries) {
      const value = item?.[itemKey];
      if (!isAvailableValue(value)) {
        isValid = false;
        break;
      }
      result[resultKey] = value;
    }
    if (isValid) {
      return result;
    }
  }

  return null;
};
